import type { CodeEditorDensity } from './types';

/** Default editor height — shared between the Suspense fallback and the impl. */
export const DEFAULT_HEIGHT = '200px';

/** Background colors for each theme — used by the Suspense fallback. */
export const THEME_BACKGROUNDS = {
  light: '#fafafa',
  dark: '#242651',
} as const;

/**
 * Each theme's gutter while `shadedGutter` is set, set off from the text. The
 * light one is the embedded app's Ace gutter; the dark one sits as far below
 * the dark background as the light one sits below the light background.
 */
export const SHADED_GUTTER_BACKGROUNDS = {
  light: '#ebf0f1',
  dark: '#1d1f46',
} as const;

/**
 * Monaco's default line height at our font size. Used only where the real
 * value isn't available yet — the Suspense fallback, and the first render
 * before the editor reports its own.
 */
export const FALLBACK_LINE_HEIGHT = 18;

type DensityMetrics = {
  fontSize: number;
  /** The height of a line. `0` leaves Monaco to work it out from the font size. */
  lineHeight: number;
  /**
   * The editor's own vertical padding. Monaco's `getContentHeight` already
   * includes it, so anything sizing a box to hold a number of lines adds it too.
   */
  padding: { top: number; bottom: number };
  /** The digits the gutter makes room for before it widens. */
  lineNumbersMinChars: number;
};

/**
 * The type size, line height, padding and gutter width of each density.
 * `regular` is Monaco's own sizing at our font size (5 is Monaco's default
 * gutter width); `compact` matches the Ace editor the embedded app renders.
 */
export const DENSITY_METRICS = {
  regular: { fontSize: 13, lineHeight: 0, padding: { top: 12, bottom: 12 }, lineNumbersMinChars: 5 },
  compact: { fontSize: 12, lineHeight: 17, padding: { top: 8, bottom: 8 }, lineNumbersMinChars: 2 },
} as const satisfies Record<CodeEditorDensity, DensityMetrics>;

/** A density's line height before the editor reports its own. */
export function estimatedLineHeight(density: CodeEditorDensity): number {
  const { lineHeight } = DENSITY_METRICS[density];
  return lineHeight > 0 ? lineHeight : FALLBACK_LINE_HEIGHT;
}

/** The height, in px, of a box holding `lines` lines plus the editor padding. */
export function linesToHeight(lines: number, lineHeight: number, density: CodeEditorDensity = 'regular'): number {
  const { padding } = DENSITY_METRICS[density];
  return lines * lineHeight + padding.top + padding.bottom;
}
